import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	ActionListItem,
	DropOverActionList,
	Form,
	FormattedText,
	ReorderableList,
	ReorderableListAddButtonClickEventArgs,
	ToolbarAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import { ActionKind } from "client_legacy/ndaw/actions";
import { SearchAndSort, sortKindToNameMap } from "./SearchAndSort";
import {
	getSearchSettings,
	SearchNodeKind,
	SearchNodeViewModel,
	ScopeSearchNodeViewModel,
	SearchSortKind,
	SearchSettingsViewModel,
	saveSearchSettings
} from "client/Api/SearchSettings/searchSettings";
import { getClientId, setClientId } from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";
import { EMPTY_ARRAY, isNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { SearchIntegrationKind, GlobalSearchScopeKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	FormModel,
	getItemName,
	GroupItemFormModel,
	IntegrationFlowData,
	NodeItemFormModel,
	PredefinedScopeData,
	ScopeItemFormModel,
	ScopesData
} from "./common";
import { GroupItem } from "./GroupItem";
import { ScopeItem } from "./ScopeItem";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { contains, normalizeForSearch } from "client/Data/searching";
import { ItemContent } from "./ItemContent";
import { IValueInputAttachable } from "client/Forms/attachable";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { SortDirection } from "client/Api/common";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { AutoFocusState } from "client/Hooks/useAutoFocus";
import { useSearchNodesStore } from "client/Features/Layout/TitleBar/GlobalSearch/useSearchNodesStore";
import { FetchCallback } from "client/zustand/createAutoFetchStore";
import { Coordinate } from "client/Misc/commonTypes";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const SearchSettingsPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>(initialSearchTerm);
	const [clientIdToAutoFocus, setClientIdToAutoFocus] = useState<Guid | null>(null);
	const [formData, setFormData] = useState<FormModel>(initialModel);
	const [initialFormData, setInitialFormData] = useState<FormModel>(initialModel);
	const [nodesData, setNodesData] = useState<readonly SearchNodeViewModel[]>(EMPTY_ARRAY);
	const [addMenuPosition, setAddMenuPosition] = useState<Coordinate | null>(null);

	const addButtonRef: RefObject<HTMLButtonElement | null> = useRef(null);

	useActiveSettingsMenuItem(SettingsPageKind.GlobalSearch);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const fetchSearchNodes: FetchCallback = useSearchNodesStore(x => x.fetch);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const scopesData = useMemo(
		(): ScopesData => {
			return getScopesData(nodesData);
		},
		[nodesData]
	);

	const nodesAttachable: IValueInputAttachable<readonly NodeItemFormModel[]> = formHandler
		.for("nodes");

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm.value),
		[searchTerm]
	);

	const autoFocusState = useMemo(
		(): AutoFocusState<Guid> => {
			return {
				itemIdToFocus: clientIdToAutoFocus,
				changeTo: (clientId) => setClientIdToAutoFocus(clientId)
			};
		},
		[clientIdToAutoFocus]
	);

	const groups = useMemo(
		(): readonly GroupItemFormModel[] => formData
			.nodes
			.filter(
				x => x.kind === SearchNodeKind.Group
			),
		[formData.nodes]
	);

	const doesMatchSearchTerm = useCallback(
		(item: NodeItemFormModel): boolean => {
			if (normalizedSearchTerm === "") {
				return true;
			}

			const doesNameContainSearchTerm: boolean = contains(
				getItemName(
					item,
					scopesData
				),
				normalizedSearchTerm
			);

			if (doesNameContainSearchTerm) {
				return true;
			}

			if (item.kind === SearchNodeKind.Group) {
				const doesAnyChildMatchSearchTerm: boolean = item
					.groupData
					.scopes
					.some(doesMatchSearchTerm);

				if (doesAnyChildMatchSearchTerm) {
					return true;
				}
			}

			return false;
		},
		[
			normalizedSearchTerm,
			scopesData
		]
	);

	const saveHandler = useCallback(
		async(): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			try {
				await saveSearchSettings(formData, globalCancellation);
				alertManager.showSuccess(localize("_ChangesSaved"));
				setInitialFormData(formData);
				setIsBusy(false);
				fetchSearchNodes({ forceReload: true });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation
					.tryProcessServerError(e)
				) {
					console.error(e);
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsBusy(false);
			}

		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			fetchSearchNodes
		]
	);

	const cancelHandler = useCallback(
		(): void => {
			setFormData(initialFormData);
		},
		[initialFormData]
	);

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			const activeItem: NodeItemFormModel | undefined = formData.nodes[activeIndex];

			if (!activeItem) {
				console.error(`Item with index ${activeIndex} does not exist`);
			}

			return getItemName(
				activeItem,
				scopesData
			);
		},
		[
			formData.nodes,
			scopesData
		]
	);

	const sortKindChangeHandler = useCallback(
		(sortKind: SearchSortKind): void => {
			const sortKindAttachable: IValueInputAttachable<SearchSortKind> = formHandler
				.for("sortKind");

			sortKindAttachable.reducer
				.setValue(sortKind);

			sortKindAttachable.validation
				.triggerInputFinished();

			const nodesAttachable: IValueInputAttachable<readonly NodeItemFormModel[]> = formHandler
				.for("nodes");

			const sortedNodes: readonly NodeItemFormModel[] = reorderNodes(
				nodesAttachable.value,
				sortKind,
				scopesData
			);

			nodesAttachable
				.reducer
				.setValue(sortedNodes);

			nodesAttachable
				.validation
				.triggerInputFinished();
		},
		[
			formHandler,
			scopesData
		]
	);

	const itemReorderHandler = useCallback(
		(): void => {
			const from: SearchSortKind = formData.sortKind;
			const to: SearchSortKind = SearchSortKind.Custom;

			if (to === from) {
				return;
			}

			sortKindChangeHandler(to);

			alertManager.showInfo(
				<FormattedText
					text={localize("_SortingChangedFromNameToName")}
					thingsToReplace={[
						{ what: "{{From}}", filler: <b>{sortKindToNameMap[from]}</b> },
						{ what: "{{To}}", filler: <b>{sortKindToNameMap[to]}</b> }
					]}
				/>
			);
		},
		[
			alertManager,
			formData.sortKind,
			sortKindChangeHandler
		]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			itemReorderHandler();

			const {
				value
			} = nodesAttachable;

			const itemsCopy: NodeItemFormModel[] = [...value];
			const item: NodeItemFormModel = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			nodesAttachable
				.reducer
				.setValue(itemsCopy);

			nodesAttachable
				.validation
				.triggerInputFinished();
		},
		[itemReorderHandler, nodesAttachable]
	);

	const createGroupHandler = useCallback(
		(): void => {
			const newGroup: GroupItemFormModel = setClientId({
				kind: SearchNodeKind.Group,
				groupData: {
					id: null,
					name: "",
					scopes: []
				}
			});

			nodesAttachable
				.reducer
				.setValue([
					...nodesAttachable.value,
					newGroup
				]);

			nodesAttachable
				.validation
				.triggerInputFinished();

			setSearchTerm({
				value: "",
				version: newGuid()
			});

			autoFocusState
				.changeTo(getClientId(newGroup));
		},
		[autoFocusState, nodesAttachable]
	);

	const deleteGroupHandler = useCallback(
		async(clientId: Guid): Promise<void> => {
			const findGroupItemIndex: TryResult<number> = tryFindGroupItemIndex(nodesAttachable.value, clientId);

			if (!findGroupItemIndex.wasSuccessful) {
				console.error(`Couldn't find group item with client id ${clientId}`);
				return;
			}

			const {
				result: groupItemIndex
			} = findGroupItemIndex;

			const item: GroupItemFormModel = nodesAttachable
				.forIndex(groupItemIndex)
				.assertType(x => x.kind === SearchNodeKind.Group)
				.value;

			const {
				groupData
			} = item;

			const name: string = groupData
				.name;

			const result: TryResult<void> = await openConfirmationDialog({
				title: (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteName")}
						thingsToReplace={[
							{
								what: "{Name}",
								filler: name
							}
						]}
					/>
				),
				confirmText: localize("_Delete"),
				trackingId: "delete-group-confirmation-dialog",
				children: (
					<FormattedText
						text={localize("_NoteTheItemsWithinTheGroupWillNotBeDeleted")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: x => (
									<Typography weight="bold">{x}</Typography>
								)
							}
						]}
					/>
				)
			});

			if (!result.wasSuccessful) {
				return;
			}

			nodesAttachable
				.reducer
				.setValue([
					...nodesAttachable.value,
					...groupData
						.scopes
				]);

			nodesAttachable
				.reducer
				.removeAt(groupItemIndex);

			nodesAttachable
				.validation
				.triggerInputFinished();
		},
		[
			nodesAttachable,
			openConfirmationDialog
		]
	);

	const moveToGroupHandler = useCallback(
		(clientId: Guid, targetGroupId: Guid): void => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentItemsAttachableAndItemIndex(
				clientId,
				nodesAttachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentItemsAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const item: ScopeItemFormModel = parentItemsAttachable
				.forIndex(itemIndex)
				.assertType(x => x.kind === SearchNodeKind.Scope)
				.value;

			const findTargetGroupItemIndex: TryResult<number> = tryFindGroupItemIndex(nodesAttachable.value, targetGroupId);

			if (!findTargetGroupItemIndex.wasSuccessful) {
				console.error(`Couldn't find group item with client id ${clientId}`);
				return;
			}

			const {
				result: targetGroupItemIndex
			} = findTargetGroupItemIndex;

			const targetGroupHandler: IValueInputAttachable<GroupItemFormModel> = nodesAttachable
				.forIndex(targetGroupItemIndex)
				.assertType(x => x.kind === SearchNodeKind.Group);

			targetGroupHandler
				.for("groupData")
				.for("scopes")
				.reducer
				.push(item);

			targetGroupHandler
				.for("groupData")
				.for("scopes")
				.validation
				.triggerInputFinished();

			parentItemsAttachable
				.reducer
				.removeAt(itemIndex);

			parentItemsAttachable
				.validation
				.triggerInputFinished();
		},
		[nodesAttachable]
	);

	const moveToTopHandler = useCallback(
		(clientId: Guid): void => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentItemsAttachableAndItemIndex(
				clientId,
				nodesAttachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				itemIndex,
				parentItemsAttachable
			} = findParentAndIndexResult.result;

			const item: ScopeItemFormModel = parentItemsAttachable
				.forIndex(itemIndex)
				.assertType(x => x.kind === SearchNodeKind.Scope)
				.value;

			nodesAttachable
				.reducer
				.push(item);

			nodesAttachable
				.validation
				.triggerInputFinished();

			parentItemsAttachable
				.reducer
				.removeAt(itemIndex);
		},
		[nodesAttachable]
	);

	const actions = useMemo(
		(): readonly Action[] => getActions(
			saveHandler,
			cancelHandler
		),
		[cancelHandler, saveHandler]
	);

	useEffect(
		(): (() => void) | void => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const result: SearchSettingsViewModel = await getSearchSettings(signal)
						.catch(handlePageDataLoadingError);

					setNodesData(result.nodes);

					const {
						nodes,
						sortKind
					} = convertToFormModel(result);

					const formModel: FormModel = {
						sortKind,
						nodes
					};

					setFormData(formModel);
					setInitialFormData(formModel);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}
			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return (): void => {
				cancellation.abort();
			};
		},
		[alertManager]
	);

	const toggleAddMenuHandler = useCallback(
		(args: ReorderableListAddButtonClickEventArgs): void => {
			setAddMenuPosition(
				(s) => {
					if (s) {
						return null;
					}

					return {
						x: args.clientX,
						y: args.clientY
					};
				}
			);
		},
		[]
	);

	const shouldShowTop: boolean = normalizedSearchTerm === ""
		|| contains(
			localize("_Top"),
			normalizedSearchTerm
		);

	const isSearching: boolean = Boolean(normalizedSearchTerm);

	return (
		<>
			<SettingsPage
				title={localize("_GlobalSearch")}
				actions={actions}
				actionContext={undefined}
				hasBusyIndicator={isBusy}
			>
				<SettingsPage.Section
					title={localize("_ManageCategories")}
				>
					<SettingsPage.SearchSection>
						<SearchAndSort
							sortKind={formData.sortKind}
							searchTerm={searchTerm}
							onSearch={setSearchTerm}
							onSortKindChange={sortKindChangeHandler}
						/>
					</SettingsPage.SearchSection>

					{shouldShowTop && (
						<ReorderableList.RowItemLike
							trackingId="top-item"
							hasSeparator
						>
							<ItemContent>
								{localize("_Top")}
							</ItemContent>
						</ReorderableList.RowItemLike>
					)}

					<Form onSubmit="NONE">
						<ReorderableList
							onItemMove={isSearching ? undefined : itemMoveHandler}
							hasSeparator
							getOverlayText={getOverlayTextHandler}
							trackingId="global-search-group-list"
						>
							{formHandler.for("nodes")
								.map(
									attachable => attachable
									.when(
										doesMatchSearchTerm,
										handler => handler.whenType(
											x => x.kind === SearchNodeKind.Group,
											groupAttachable => (
												<GroupItem
													key={getClientId(groupAttachable.value)}
													attachable={groupAttachable}
													groups={groups}
													autoFocusState={autoFocusState}
													scopesData={scopesData}
													isSearching={isSearching}
													doesMatchSearchTerm={doesMatchSearchTerm}
													onMoveToGroup={moveToGroupHandler}
													onDeleteGroup={deleteGroupHandler}
													onMoveToTop={moveToTopHandler}
													onItemReorder={itemReorderHandler}
												/>
											),
											scopeAttachable => (
												<ScopeItem
													key={getClientId(scopeAttachable.value)}
													attachable={scopeAttachable}
													groups={groups}
													onMoveToGroup={moveToGroupHandler}
													scopesData={scopesData}
												/>
											)
										)
									)
								)
							}
						</ReorderableList>
						<ReorderableList.AddButton
							ref={addButtonRef}
							onClick={toggleAddMenuHandler}
							trackingId="add-button"
						/>

						{(addMenuPosition && addButtonRef.current) && (
							<DropOverActionList
								target={addButtonRef.current}
								onClose={() => setAddMenuPosition(null)}
								contentAlignment="tl"
								targetAlignment={{
									kind: "absolute",
									coordinate: addMenuPosition
								}}
								menuTitle={localize("_Add")}
							>
								<ActionListItem
									onClick={() => createGroupHandler()}
									text={localize("_CreateGroup")}
									trackingId="create-group"
									icon="layer-group"
								/>
							</DropOverActionList>
						)}
					</Form>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>
			{confirmationDialog}
		</>
	);
};

SearchSettingsPage.displayName = "SearchSettingsPage";

const initialSearchTerm = {
	value: "",
	version: newGuid()
};

const initialModel: FormModel = {
	sortKind: SearchSortKind.AlphabeticalAscending,
	nodes: EMPTY_ARRAY
};

const validationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();
const itemValidationConfiguration = createValidationConfiguration<NodeItemFormModel>();

itemValidationConfiguration
	.whenType(x => x.kind === SearchNodeKind.Group)
	.forObject("groupData")
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
			.activateOn("input")
			.validateOn("input")
	);

validationConfiguration
	.forCollection("nodes")
	.forItems(
		itemValidationConfiguration,
		getClientId
	);

function getScopesData(
	nodes: readonly SearchNodeViewModel[]
): ScopesData {
	const integrationFlowsData: Map<number, IntegrationFlowData> = new Map();
	const predefinedScopesData: Map<SearchIntegrationKind, PredefinedScopeData> = new Map();

	for (const node of nodes) {
		switch(node.kind) {
			case SearchNodeKind.Scope:
				addScopeData(
					node,
					integrationFlowsData,
					predefinedScopesData
				);
				break;
			case SearchNodeKind.Group:
				for (const scope of node.scopes) {
					addScopeData(
						scope,
						integrationFlowsData,
						predefinedScopesData
					);
				}
				break;
			default:
				never(node);
		}
	}

	return {
		integrationFlowsData,
		predefinedScopesData
	};
}

function addScopeData(
	scope: ScopeSearchNodeViewModel,
	integrationFlowsData: Map<number, IntegrationFlowData>,
	predefinedScopesData: Map<SearchIntegrationKind, PredefinedScopeData>
): void {
	switch (scope.scopeKind) {
		case GlobalSearchScopeKind.Predefined:
			predefinedScopesData.set(
				scope.predefinedKind,
				{
					name: scope.name
				}
			);

			break;
		case GlobalSearchScopeKind.IntegrationFlow:
			const {
				color,
				imageDisplayInfo,
				integrationId,
				name
			} = scope;

			integrationFlowsData.set(
				integrationId,
				{
					name,
					color,
					imageDisplayInfo
				}
			);

			break;
		default:
			never(scope);
	}
}

function convertToFormModel(
	model: SearchSettingsViewModel
): FormModel {
	const {
		nodes,
		sortKind
	} = model;

	return {
		sortKind,
		nodes: nodes
			.map(convertToNodeItemFormModel)
	};
}

function convertToNodeItemFormModel(
	node: SearchNodeViewModel
): NodeItemFormModel {
	switch(node.kind) {
		case SearchNodeKind.Scope:
			return convertToScopeItemFormModel(node);
		case SearchNodeKind.Group:
			const {
				id,
				name,
				scopes
			} = node;

			return setClientId({
				kind: SearchNodeKind.Group,
				groupData: {
					id,
					name,
					scopes: scopes.map(convertToScopeItemFormModel)
				}
			});
		default:
			never(node);
	}
}

function convertToScopeItemFormModel(
	scope: ScopeSearchNodeViewModel
): ScopeItemFormModel {
	switch (scope.scopeKind) {
		case GlobalSearchScopeKind.Predefined:
			return setClientId({
				kind: SearchNodeKind.Scope,
				scopeData: {
					scope: {
						kind: GlobalSearchScopeKind.Predefined,
						predefinedKind: scope.predefinedKind
					}
				}
			});
		case GlobalSearchScopeKind.IntegrationFlow:
			const {
				integrationId,
				name
			} = scope;

			return setClientId({
				kind: SearchNodeKind.Scope,
				scopeData: {
					scope: {
						kind: GlobalSearchScopeKind.IntegrationFlow,
						integrationId,
						name
					}
				}
			});
		default:
			never(scope);
	}
}

type Action = ToolbarAction<undefined>;

function getActions(
	save: () => void,
	cancel: () => void
): readonly Action[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: () => true,
			execute: save,
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: cancel
		}
	];
}

function tryFindGroupItemIndex(
	items: readonly NodeItemFormModel[],
	targetClientId: Guid
): TryResult<number> {
	const result: number | undefined = items.findIndex(
		x => x.kind === SearchNodeKind.Group
			&& getClientId(x) === targetClientId
	);

	if (isNil(result)) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result
	};
}

type ParentAndItemIndex = {
	parentItemsAttachable: IValueInputAttachable<readonly NodeItemFormModel[]>;
	itemIndex: number;
};

function tryFindParentItemsAttachableAndItemIndex(
	clientId: Guid,
	attachable: IValueInputAttachable<readonly NodeItemFormModel[]>
): TryResult<ParentAndItemIndex> {
	let result: TryResult<ParentAndItemIndex> = {
		wasSuccessful: false
	};

	attachable
		.forEach(
			(itemHandler, index) => {
				if (result.wasSuccessful) {
					return;
				}

				if (getClientId(itemHandler.value) === clientId) {
					result = {
						wasSuccessful: true,
						result: {
							parentItemsAttachable: attachable,
							itemIndex: index
						}
					};
				} else {
					itemHandler.whenType(
						x => x.kind === SearchNodeKind.Group,
						groupHandler => {
							const tryResult = tryFindParentItemsAttachableAndItemIndex(
								clientId,
								groupHandler
									.for("groupData")
									.for("scopes")
							);

							if (tryResult.wasSuccessful) {
								result = tryResult;
							}
						}
					);
				}
			}
		);

	return result;
}

function reorderNodes(
	nodes: readonly NodeItemFormModel[],
	sortKind: SearchSortKind,
	scopesData: ScopesData
): readonly NodeItemFormModel[] {
	switch(sortKind) {
		case SearchSortKind.AlphabeticalAscending:
			return sortNodesAlphabetically(
				nodes,
				SortDirection.Ascending,
				scopesData
			);
		case SearchSortKind.AlphabeticalDescending:
			return sortNodesAlphabetically(
				nodes,
				SortDirection.Descending,
				scopesData
			);
		case SearchSortKind.Custom:
			return nodes;
		default:
			never(sortKind);
	}
};

function sortNodesAlphabetically(
	nodes: readonly NodeItemFormModel[],
	direction: SortDirection,
	scopesData: ScopesData
): readonly NodeItemFormModel[] {
	return sortBy(
		nodes,
		(x) => getItemName(
			x,
			scopesData
		)
			.toLowerCase(),
		direction
	).map(
		x => {
			if (x.kind === SearchNodeKind.Group) {
				const { groupData } = x;

				return {
					...x,
					groupData: {
						...groupData,
						scopes: sortBy(
							groupData.scopes,
							(x) => getItemName(
								x,
								scopesData
							)
								.toLowerCase(),
							direction
						)
					}
				};
			}

			return x;
		}
	);
}

export const SearchSettingsRouting = {
	page: "/SearchSettings"
};